import { FC, useCallback, useEffect, useMemo } from 'react';
import { SummaryTabBlockChildProps } from '../summary-tab/SummaryTab';
import { Box, Group, Text, Title, Paper, LoadingOverlay } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { ReactFlow } from '@xyflow/react';

import '@xyflow/react/dist/style.css';

// Import our custom components and types
import { PedigreePersonNodeData } from './types';
import { PersonNode } from './components/PersonNode';
import { RelationshipNode } from './components/RelationshipNode';
import { PedigreeLegend } from './components/PedigreeLegend';
import { NodeContextMenu } from './components/NodeContextMenu';
import RelationshipToChildEdge from './edges/RelationshipToChildEdge';

import { PersonModal } from './components/PersonModal';
import SpouseToRelationshipEdge from './edges/SpouseToRelationshipEdge';
import { PedigreeProvider } from './context/PedigreeProvider';
import { PedigreeControls } from './components/PedigreeControls';
import { useReactflowState } from './hooks/useReactflowState';
import { useContextMenu } from './hooks/useContextMenu';
import { usePedigreeContext } from './context/usePedigreeContext';
import { useGraphData } from './hooks/useGraphData';
import { RelationshipModal } from './components/RelationshipModal';
import { usePedigreeData } from './context/usePedigreeData';

import classes from './Pedigree.module.css';

// -----------------------------------------------------------------------------
const PedigreeInternal: FC<SummaryTabBlockChildProps> = ({
  minimizedView,
  mode = 'edit',
}) => {
  const { t } = useTranslation('pedigree');
  const { nodes, edges, onNodesChange, onEdgesChange } = useReactflowState();

  const { contextMenu, openContextMenu, closeContextMenu } = useContextMenu();
  const {
    selectedNodeId,
    setSelectedNodeId,
    mode: currentMode,
    setMode,
    relationshipSelectionMode,
    setRelationshipSelectionMode,
  } = usePedigreeContext();

  const { finishedInitialLoading } = usePedigreeData();
  const { addRelationship } = useGraphData();

  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (mode !== currentMode) {
      setMode(mode);
    }
  }, [currentMode, mode, setMode]);

  //----------------------------------------------------------------------------
  const onPaneClick = useCallback(() => {
    closeContextMenu();
    setSelectedNodeId(null);

    if (relationshipSelectionMode.isActive) {
      setRelationshipSelectionMode({
        isActive: false,
        firstNodeId: null,
      });
    }
  }, [
    closeContextMenu,
    setSelectedNodeId,
    relationshipSelectionMode,
    setRelationshipSelectionMode,
  ]);

  //----------------------------------------------------------------------------
  const onNodeContextMenuHandler = useCallback(
    (event: React.MouseEvent, node: any) => {
      event.preventDefault();

      if (relationshipSelectionMode.isActive && node.type === 'person') {
        const secondNodeId = Number(node.id);
        const firstNodeId = relationshipSelectionMode.firstNodeId;

        if (firstNodeId && firstNodeId !== secondNodeId) {
          addRelationship(firstNodeId, secondNodeId);
        }

        setRelationshipSelectionMode({
          isActive: false,
          firstNodeId: null,
        });
        return;
      }

      // Disable context menu in view mode
      if (mode === 'view') {
        return;
      }

      const centerX = window.innerWidth / 2;
      const centerY = window.innerHeight / 2;

      const nodeType =
        node?.type === 'relationship' ? 'relationship' : 'person';

      if (nodeType === 'relationship') {
        setSelectedNodeId(Number(node.id));
        return;
      }

      openContextMenu(Number(node.id), { x: centerX, y: centerY }, nodeType);
    },
    [
      mode,
      openContextMenu,
      setSelectedNodeId,
      relationshipSelectionMode,
      addRelationship,
      setRelationshipSelectionMode,
    ]
  );

  // ---------------------------------------------------------------------------
  const nodeTypes = useMemo(
    () => ({
      person: (nodeProps: any) => (
        <PersonNode
          {...nodeProps}
          isSelected={selectedNodeId === Number(nodeProps.id)}
          style={{
            ...nodeProps.style,
            cursor: mode === 'view' ? 'default' : 'pointer',
          }}
        />
      ),
      relationship: (nodeProps: any) => (
        <RelationshipNode
          {...nodeProps}
          isSelected={selectedNodeId === Number(nodeProps.id)}
          style={{
            ...nodeProps.style,
            cursor: mode === 'view' ? 'default' : 'pointer',
          }}
        />
      ),
    }),
    [mode, selectedNodeId]
  );

  // Edge types configuration
  const edgeTypes = useMemo(
    () => ({
      'relationship-to-child': RelationshipToChildEdge,
      'spouse-to-relationship': SpouseToRelationshipEdge,
    }),
    []
  );

  //----------------------------------------------------------------------------
  const renderMinimizedView = () => {
    const personNodes = nodes.filter((node: any) => node.type === 'person');
    const totalConditions = new Set(
      personNodes.flatMap(
        (node: any) =>
          (node.data as PedigreePersonNodeData).conditions?.map(
            (c: any) => c.id
          ) || []
      )
    ).size;
    return (
      <Paper p="md" radius="md" bg="gray.1" ta="center">
        <Title order={5} mb="xs">
          {t('title')}
        </Title>
        <Text size="xs" c="dimmed" mb="xs">
          {personNodes.length} {t('minimizedView.familyMembers')} •{' '}
          {totalConditions} {t('minimizedView.conditionsTracked')}
        </Text>
        <Group justify="center" gap="xs">
          <Text size="xs" c="dark.3">
            {`🔷 ${t('minimizedView.grandparents')}: ${
              personNodes.filter((n: any) => {
                const data = n.data as PedigreePersonNodeData;
                return data.label?.includes('GF') || data.label?.includes('GM');
              }).length
            }`}
          </Text>
          <Text size="xs" c="dark.3">
            {`👨‍👩‍👧‍👦 ${t('minimizedView.parents')}: ${
              personNodes.filter((n: any) => {
                const data = n.data as PedigreePersonNodeData;
                return (
                  data.label?.includes('Father') ||
                  data.label?.includes('Mother')
                );
              }).length
            } `}
          </Text>
          <Text size="xs" c="dark.3">
            {`👶 ${t('minimizedView.children')}: ${
              personNodes.filter((n: any) => {
                const data = n.data as PedigreePersonNodeData;
                return (
                  data.isProband ||
                  data.label?.includes('Child') ||
                  data.label?.includes('Sister') ||
                  data.label?.includes('Brother')
                );
              }).length
            }`}
          </Text>
        </Group>
      </Paper>
    );
  };

  //----------------------------------------------------------------------------
  const renderFullView = () => {
    return (
      <Box pos="relative" w="100%" h="80vh">
        <LoadingOverlay
          visible={!finishedInitialLoading}
          overlayProps={{ backgroundOpacity: 0.9 }}
        />

        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onNodeClick={onNodeContextMenuHandler}
          onPaneClick={onPaneClick}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          nodesDraggable={false}
          nodesConnectable={false}
          edgesFocusable={false}
          elementsSelectable={false}
          fitView
          attributionPosition="bottom-left"
          proOptions={{ hideAttribution: true }}
          minZoom={0.3}
        >
          <PedigreeControls
            finishedInitialRender={finishedInitialLoading}
            numberOfNodes={nodes.length}
          />{' '}
        </ReactFlow>

        {relationshipSelectionMode.isActive && (
          <Box className={classes.relationshipSelectionBanner}>
            <Text size="sm" fw={500}>
              {t('relationshipSelection.instruction')}
            </Text>
          </Box>
        )}

        <PedigreeLegend />
        {mode !== 'view' && (
          <>
            <PersonModal onClose={() => setSelectedNodeId(null)} />
            <RelationshipModal onClose={() => setSelectedNodeId(null)} />
          </>
        )}

        {mode !== 'view' && (
          <NodeContextMenu
            isOpen={contextMenu.isOpen}
            position={contextMenu.position}
            selectedNode={contextMenu.nodeId}
            onClose={closeContextMenu}
            nodeType={contextMenu.nodeType}
          />
        )}
      </Box>
    );
  };

  //----------------------------------------------------------------------------
  return minimizedView ? renderMinimizedView() : renderFullView();
};

// -----------------------------------------------------------------------------
// Main component with context provider
const Pedigree: FC<SummaryTabBlockChildProps> = (props) => {
  return (
    <PedigreeProvider>
      <PedigreeInternal {...props} />
    </PedigreeProvider>
  );
};

export default Pedigree;
